import { useData } from "../hooks/useData";
import { Change, SITE_COLOR, SITE_LABEL, n0, type Site, type WebStatus } from "../components/Web";

/** Web on the phone: this week at a glance. The full reports are on the desktop. */
interface Summary {
  sites: Record<Site, { users: number; views: number; users_change: number | null }>;
  top_pages: { path: string; title: string; url: string; views: number }[];
  top_queries: { query: string; clicks: number; impressions: number }[];
  mentions: { query: string; impressions: number; match: { kind: string; id: string; name: string } }[];
  rising_market: { country: string; users: number; change: number | null; market: { flag: string | null; deals: number } } | null;
  fetched_at: string;
}

export default function PhoneWeb({ go }: { go: (s: string, id?: string | null) => void }) {
  const { data: st } = useData<WebStatus>("/api/web/status");
  const { data, error } = useData<Summary>(st?.analytics ? "/api/web/phone" : null);
  if (st && !st.analytics) return <div className="ph-pad"><div className="ph-card ph-empty">Website statistics aren't connected yet. An admin sets them up under Web on the desktop.</div></div>;
  if (error && !data) return <div className="ph-pad"><div className="ph-note err">{error}</div></div>;
  if (!data) return <div className="ph-pad"><div className="ph-skel" /><div className="ph-skel" /></div>;
  return (
    <div className="ph-pad">
      <div className="ph-sub"><span>Last 7 days · updated {new Date(data.fetched_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span></div>
      <div className="ph-webtiles">
        {(["www", "info"] as Site[]).map(s => (
          <div key={s} className="ph-card ph-webtile">
            <span className="ph-row-s"><i className="wb-key" style={{ background: SITE_COLOR[s] }} />{s === "www" ? "www" : "info"}</span>
            <b>{n0(data.sites[s].users)}</b>
            <span className="ph-row-s">visitors <Change v={data.sites[s].users_change} /></span>
          </div>
        ))}
      </div>
      {data.rising_market && (
        <div className="ph-note">
          {data.rising_market.market.flag} <b>{data.rising_market.country}</b> visitors up {data.rising_market.change}% ({n0(data.rising_market.users)})
          {data.rising_market.market.deals ? ` — ${data.rising_market.market.deals} deal${data.rising_market.market.deals === 1 ? "" : "s"} there` : ""}.
        </div>
      )}
      {data.mentions.length > 0 && <>
        <h2 className="ph-h2">Searches naming our accounts or markets</h2>
        <div className="ph-card">
          {data.mentions.map(m => (
            <button key={m.query} className="ph-row ph-row-btn" onClick={() => go(m.match.kind === "principal" ? "principal" : m.match.kind === "market" ? "countries" : "clients", m.match.kind === "market" ? null : m.match.id)}>
              <span className="ph-row-m"><span className="ph-row-t">“{m.query}”</span><span className="ph-row-s">{m.match.name} · shown {n0(m.impressions)}×</span></span>
              <span className="ph-due">›</span>
            </button>
          ))}
        </div>
      </>}
      <h2 className="ph-h2">Most read on info</h2>
      <div className="ph-card">
        {data.top_pages.map(p => (
          <a key={p.path} className="ph-row ph-row-btn ph-a" href={p.url} target="_blank" rel="noreferrer">
            <span className="ph-row-m"><span className="ph-row-t ph-wrap">{p.title || p.path}</span></span>
            <span className="ph-due">{n0(p.views)}</span>
          </a>
        ))}
        {!data.top_pages.length && <div className="ph-empty">No page views this week.</div>}
      </div>
      {data.top_queries.length > 0 && <>
        <h2 className="ph-h2">Top Google searches (www, 4 weeks)</h2>
        <div className="ph-card">
          {data.top_queries.map(q => (
            <div key={q.query} className="ph-row"><span className="ph-row-m"><span className="ph-row-t">{q.query}</span><span className="ph-row-s">{n0(q.clicks)} clicks · shown {n0(q.impressions)}×</span></span></div>
          ))}
        </div>
      </>}
      <p className="ph-fine">Pages, searches, countries and newsletter visits in full: Web on the desktop ({SITE_LABEL.www} and {SITE_LABEL.info}).</p>
    </div>
  );
}
